<template>
   <AppLayout>
      <template #main>
         <div class="mx-auto max-w-7xl px-6 lg:px-8 mt-24">
            <SearchBar
               @on-search="performSearch"
               @clear-search="onSearchCleared"
               v-model="searchTerm"
            />
            <div class="flex flex-wrap justify-start gap-2 mt-3">
               <div
                  v-for="eventType in eventTypes.data"
                  :key="eventType.id"
                  class="inline-flex items-center rounded-md px-2 py-2 text-sm font-medium text-gray-600 bg-gray-100"
                  :class="{'bg-primary-color text-white': selectedEventTypeId === eventType.id}"
               >
                  <span
                     @click.prevent="filterEventsByType(eventType.id)"
                  >
                     {{ eventType.name }}
                  </span>
               </div>
            </div>
         </div>


         <div
            class="mx-auto max-w-7xl w-full min-h-7xl overflow-hidden grid grid-cols-1 gap-y-44 py-6  mb-24"
         >
            <div
               v-if="noEvents && !isLoading"
               class="px-36 py-36 text-center"
            >
               <div v-if="isEmpty(searchTerm)">
                  <h2 class="font-bold text-5xl">
                     {{ $t('events.no_upcoming_events') }}
                  </h2>
                  <p class="text-lg p-8">
                     {{ $t('events.no_upcoming_events_description') }}
                  </p>
               </div>
               <div v-else>
                  <h2
                     class="font-normal text-4xl"
                     v-html="noEventsText"
                  />
               </div>
            </div>
            <div v-else>
               <div
                  class="justify-center px-4 sm:px-6 lg:px-8 py-16 sm:py-6"
               >
                  <div
                     class="items-center grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-3 lg:gap-x-8"
                     v-if="!isLoading"
                  >
                     <EventItem
                        v-motion-fade-visible-once
                        v-for="calendarEvent in eventsResource.data"
                        :key="calendarEvent.id"
                        :calendar-event="calendarEvent"
                     />
                  </div>
                  <div
                     class="items-center grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-3 lg:gap-x-8"
                     v-else
                  >
                     <Skeleton
                        v-for="n in 6"
                        :key="n"
                        class="!w-full !h-80"
                     />
                  </div>
               </div>
            </div>
         </div>

         <SponsorsSection />
      </template>
   </AppLayout>
</template>

<script setup>
   import AppLayout from '@/Layouts/AppLayout.vue';
   import EventItem from '@/Components/Events/CalendarEvent.vue';
   import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
   import { isEmpty } from 'lodash';
   import SearchBar from '@/Components/SearchBar.vue';
   import { trans } from 'laravel-vue-i18n';
   import { router } from '@inertiajs/vue3';
   import SponsorsSection from '@/Components/SponsorsSection.vue';
   import Skeleton from 'primevue/skeleton';

   const props = defineProps({
      eventTypes: Object,
      events: Object,
      showAllEvents: Boolean,
      filters: Object,
   });


   const selectedEventTypeId = ref(!isEmpty(props.filters?.event_type_id ?? null) ? parseInt(props.filters.event_type_id) : null);

   const searchTerm = ref('');
   const noEventsText = ref(trans('blog.no_posts'));

   const eventsResource = ref(props.events);
   const isLoading = ref(false);
   const allPagesLoaded = ref(isEmpty(props.events.links.next));


   const noEvents = computed(() => {
      return isEmpty(eventsResource.value.data);
   });

   const fetchEvents = async (url) => {
      isLoading.value = true;
      const response = await axios.get(url);
      eventsResource.value = {
         links: response.data.links,
         data: [...eventsResource.value.data, ...response.data.data],
      };
      isLoading.value = false;
      allPagesLoaded.value = isEmpty(eventsResource.value.links.next);
   };
   
   const performSearch = async (searchText) => {
      searchTerm.value = searchText;
      eventsResource.value = {
         data: [],
      };
      await fetchEvents(route('events.index', {
         filter: {
            search: searchTerm.value,
         },
      }));
      if (eventsResource.value.data.length <= 0) {
         noEventsText.value = trans('events.no_events_found', {searchTerm: searchText});

      }
   };
   const onSearchCleared = (searchText) => {
      eventsResource.value = {
         data: [],
      };
      searchTerm.value = '';
   };

   const handleScroll = async (e) => {
      const pixelsFromBottom =
         document.documentElement.offsetHeight -
         document.documentElement.scrollTop -
         window.innerHeight;

      if (
         pixelsFromBottom < 600 &&
         isLoading.value === false &&
         allPagesLoaded.value === false
      ) {
         await fetchEvents(eventsResource.value.links.next);
      }
   };

   onMounted(() => window.addEventListener('scroll', handleScroll));

   onUnmounted(() => window.removeEventListener('scroll', handleScroll));
   
   watch(searchTerm, async (newValue, oldValue) => {
      if (isEmpty(searchTerm.value)) {
         onSearchCleared();
         await fetchEvents(route('events.index'));
      }
   });
   
   const filterEventsByType = (eventTypeId) => {
      if (eventTypeId === selectedEventTypeId.value) {
         selectedEventTypeId.value = null;
      } else {
         selectedEventTypeId.value = eventTypeId;
      }
      const queryParams = {};

      if (selectedEventTypeId.value !== null) {
         queryParams['filter[event_type_id]'] = selectedEventTypeId.value;
      }

      router.visit(route('events.index'), {
         data: queryParams,
         only: ['events', 'filters'],
         preserveScroll: true,

      });    
   };
   
   
</script>
